import React, { useLayoutEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from 'react-router-dom';
import './App.css';
import { Toaster } from './components/ui/sonner';
import Header from './components/Header';
import Hero from './components/Hero';
import Products from './components/Products';
import MergedSection from './components/MergedSection';
import Testimonials from './components/Testimonials';
import EposShowcase from './components/EposShowcase';
import FAQ from './components/FAQ';
import Contact from './components/Contact';
import Footer from './components/Footer';
import N86TerminalGuide from './pages/N86TerminalGuide';
import N96TerminalGuide from './pages/N96TerminalGuide';
import AcceptingPaymentsFAQs from './pages/AcceptingPaymentsFAQs';
import HelpCentre from './pages/HelpCentre';
import FraudPrevention from './pages/FraudPrevention';
import Glossary from './pages/Glossary';
import BusinessForms from './pages/BusinessForms';
import TechnicalDocumentation from './pages/TechnicalDocumentation';
import PaymentIntegration from './pages/PaymentIntegration';
import ApiReference from './pages/ApiReference';
import ApiOverview from './pages/ApiOverview';
import SalePreAuth from './pages/SalePreAuth';
import ThreeDSecureAuth from './pages/ThreeDSecureAuth';
import ThreeDSecureConfirm from './pages/ThreeDSecureConfirm';
import CompletePreAuth from './pages/CompletePreAuth';
import VoidPreAuth from './pages/VoidPreAuth';
import Refund from './pages/Refund';
import RecurringBilling from './pages/RecurringBilling';
import HostedPaymentsPage from './pages/HostedPaymentsPage';
import HostedPaymentsPageToken from './pages/HostedPaymentsPageToken';
import DCC from './pages/DCC';
import GooglePay from './pages/GooglePay';
import Notifications from './pages/Notifications';
import ConfirmationNotification from './pages/ConfirmationNotification';
import ChargebackNotification from './pages/ChargebackNotification';
import FraudNotification from './pages/FraudNotification';
import Troubleshooting from './pages/Troubleshooting';
import TsCommonErrors from './pages/TsCommonErrors';
import TsWebhookDelivery from './pages/TsWebhookDelivery';
import TsIntegrationIssues from './pages/TsIntegrationIssues';
import Appendix from './pages/Appendix';
import AppendixErrorCodes from './pages/AppendixErrorCodes';
import AppendixUrlEncoding from './pages/AppendixUrlEncoding';
import AppendixCountryCodes from './pages/AppendixCountryCodes';
import AppendixCurrencyCodes from './pages/AppendixCurrencyCodes';
import AppendixTestCards from './pages/AppendixTestCards';
import MerchantLoginACP from './pages/MerchantLoginACP';
import AcpPortalLogin from './pages/AcpPortalLogin';
import MerchantLoginACQ from './pages/MerchantLoginACQ';
import AcqPortalLogin from './pages/AcqPortalLogin';
import ProductsHub from './pages/Products';
import ProductsPosCardMachines from './pages/ProductsPosCardMachines';
import ProductsEposAndMini from './pages/ProductsEposAndMini';
import ProductsSoftPosMpos from './pages/ProductsSoftPosMpos';
import ProductsEcommerce from './pages/ProductsEcommerce';
import ProductsTransactionReporting from './pages/ProductsTransactionReporting';
import GetStartedQuote from './pages/GetStartedQuote';
import GetStartedEpos from './pages/GetStartedEpos';
import GetStartedSoftpos from './pages/GetStartedSoftpos';
import GetStartedEcommerce from './pages/GetStartedEcommerce';
import GetStartedTransactionReporting from './pages/GetStartedTransactionReporting';
import AboutCompany from './pages/AboutCompany';
import AboutPartner from './pages/AboutPartner';
import AboutSocialResponsibility from './pages/AboutSocialResponsibility';
import AboutModernSlavery from './pages/AboutModernSlavery';
import AboutPressMedia from './pages/AboutPressMedia';
import ContactSales from './pages/ContactSales';
import AboutPlaceholder from './pages/AboutPlaceholder';
import ComplaintsResolution from './pages/ComplaintsResolution';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import SCAStatement from './pages/SCAStatement';
import CookieConsent from './components/CookieConsent';

function HomePage() {
  return (
    <>
      <Header />
      <Hero />
      <Products />
      <MergedSection />
      <Testimonials />
      <EposShowcase /> {/* Can be moved above Testimonials if needed */}
      <FAQ />
      <Contact />
      <Footer />
    </>
  );
}

// Module-level cache that persists across React renders, keyed by pathname+hash.
const scrollMap = {};
let lastSeenScrollY = 0;
let lastSeenKey = null;
// When a bottom CTA fires window.__resetSourceScroll(), we set this flag so
// the next ScrollToTop effect saves 0 (top) into scrollMap for the page we
// are leaving — instead of whatever lastSeenScrollY happens to be at that
// instant. This bypasses race conditions caused by CSS `scroll-behavior:
// smooth` (which makes window.scrollTo animate, so the scroll-listener
// keeps overwriting lastSeenScrollY back to the old bottom value while
// React is mid-navigation).
let forceZeroForSource = false;

// Continuously track scroll position so we know where the user is when they
// navigate away. Attached once at module init.
if (typeof window !== 'undefined') {
  window.addEventListener(
    'scroll',
    () => {
      lastSeenScrollY = window.scrollY;
    },
    { passive: true }
  );
  if ('scrollRestoration' in window.history) {
    // We manage scroll manually now.
    window.history.scrollRestoration = 'manual';
  }
  // Helper exposed to components so a CTA can mark "when the user returns
  // to this page, land them at the top, not where the CTA was clicked".
  // Called BEFORE the actual navigation runs.
  window.__resetSourceScroll = () => {
    forceZeroForSource = true;
    lastSeenScrollY = 0;
    // Instant scroll bypasses CSS `scroll-behavior: smooth`, so the user
    // doesn't see a janky animation as the route changes.
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    } catch (_) {
      window.scrollTo(0, 0);
    }
  };
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType(); // 'PUSH' | 'POP' | 'REPLACE'
  const newKey = pathname + hash;

  useLayoutEffect(() => {
    // 1. Save the scroll position of the page we're LEAVING (lastSeenKey)
    //    so that if the user comes back via POP we can restore it. If a
    //    bottom CTA just fired __resetSourceScroll(), save 0 instead — so
    //    the user lands at the top of the source page when they come back.
    if (lastSeenKey !== null) {
      scrollMap[lastSeenKey] = forceZeroForSource ? 0 : lastSeenScrollY;
    }
    forceZeroForSource = false;

    // 2. Now decide how to scroll the page we're ENTERING.
    if (navType === 'POP') {
      // Restore previously saved scroll position synchronously BEFORE the
      // browser paints — so the user never sees a flash of scrollY=0
      // followed by a "jump" to their saved spot. The `'instant'` behavior
      // overrides the global CSS `scroll-behavior: smooth`, guaranteeing
      // a zero-animation restoration (Teya-style: just a flash and you're
      // back where you were).
      const saved = scrollMap[newKey] || 0;
      try {
        window.scrollTo({ top: saved, left: 0, behavior: 'instant' });
      } catch (_) {
        window.scrollTo(0, saved);
      }
      lastSeenScrollY = saved;

      // Safety net for tall/image-heavy pages (e.g. the homepage) whose
      // full height isn't laid out yet at restore time — the initial
      // scrollTo clamps to maxReachable, so we keep nudging via rAF until
      // the document grows tall enough or we give up after ~640ms.
      let attempts = 0;
      const maxAttempts = 40;
      const tryRestore = () => {
        const maxReachable =
          (document.documentElement.scrollHeight || document.body.scrollHeight) -
          window.innerHeight;
        if (window.scrollY === saved || attempts >= maxAttempts) return;
        if (maxReachable >= saved) {
          try {
            window.scrollTo({ top: saved, left: 0, behavior: 'instant' });
          } catch (_) {
            window.scrollTo(0, saved);
          }
          lastSeenScrollY = saved;
          return;
        }
        attempts += 1;
        requestAnimationFrame(tryRestore);
      };
      requestAnimationFrame(tryRestore);
    } else if (hash) {
      // Forward navigation to a hash anchor — defer until target renders.
      const t = setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        else window.scrollTo(0, 0);
      }, 80);
      lastSeenKey = newKey;
      lastSeenScrollY = 0;
      return () => clearTimeout(t);
    } else {
      // Forward navigation (PUSH / REPLACE) without hash — always jump
      // instantly to the top. No animation on desktop or mobile.
      // The instant `behavior` overrides the page's CSS
      // `scroll-behavior: smooth` so the route change feels snappy.
      try {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      } catch (_) {
        window.scrollTo(0, 0);
      }
      lastSeenScrollY = 0;
    }

    lastSeenKey = newKey;
  }, [newKey, navType, hash]);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <div className="App">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/n86-terminal-guide" element={<N86TerminalGuide />} />
          <Route path="/n96-terminal-guide" element={<N96TerminalGuide />} />
          <Route path="/accepting-payments-faqs" element={<AcceptingPaymentsFAQs />} />
          <Route path="/help-centre" element={<HelpCentre />} />
          <Route path="/fraud-prevention" element={<FraudPrevention />} />
          <Route path="/glossary" element={<Glossary />} />
          <Route path="/business-forms" element={<BusinessForms />} />
          <Route path="/technical-documentation" element={<TechnicalDocumentation />} />
          <Route path="/payment-integration" element={<PaymentIntegration />} />
          <Route path="/technical-documentation/api-reference" element={<ApiReference />} />
          <Route path="/technical-documentation/api-reference/overview" element={<ApiOverview />} />
          <Route path="/technical-documentation/api-reference/sale-pre-auth" element={<SalePreAuth />} />
          <Route path="/technical-documentation/api-reference/3d-secure-auth" element={<ThreeDSecureAuth />} />
          <Route path="/technical-documentation/api-reference/3d-secure-confirm" element={<ThreeDSecureConfirm />} />
          <Route path="/technical-documentation/api-reference/complete-pre-auth" element={<CompletePreAuth />} />
          <Route path="/technical-documentation/api-reference/void-pre-auth" element={<VoidPreAuth />} />
          <Route path="/technical-documentation/api-reference/refund" element={<Refund />} />
          <Route path="/technical-documentation/api-reference/recurring-billing" element={<RecurringBilling />} />
          <Route path="/technical-documentation/api-reference/hosted-payment-page" element={<HostedPaymentsPage />} />
          <Route path="/technical-documentation/api-reference/hosted-payment-token" element={<HostedPaymentsPageToken />} />
          <Route path="/technical-documentation/api-reference/dcc" element={<DCC />} />
          <Route path="/technical-documentation/api-reference/google-pay" element={<GooglePay />} />
          <Route path="/technical-documentation/notifications" element={<Notifications />} />
          <Route path="/technical-documentation/notifications/confirmation" element={<ConfirmationNotification />} />
          <Route path="/technical-documentation/notifications/chargeback" element={<ChargebackNotification />} />
          <Route path="/technical-documentation/notifications/fraud" element={<FraudNotification />} />
          <Route path="/technical-documentation/troubleshooting" element={<Troubleshooting />} />
          <Route path="/technical-documentation/troubleshooting/common-errors" element={<TsCommonErrors />} />
          <Route path="/technical-documentation/troubleshooting/webhook-delivery" element={<TsWebhookDelivery />} />
          <Route path="/technical-documentation/troubleshooting/integration-issues" element={<TsIntegrationIssues />} />
          <Route path="/technical-documentation/appendix" element={<Appendix />} />
          <Route path="/technical-documentation/appendix/error-codes" element={<AppendixErrorCodes />} />
          <Route path="/technical-documentation/appendix/url-encoding" element={<AppendixUrlEncoding />} />
          <Route path="/technical-documentation/appendix/country-codes" element={<AppendixCountryCodes />} />
          <Route path="/technical-documentation/appendix/currency-codes" element={<AppendixCurrencyCodes />} />
          <Route path="/technical-documentation/appendix/test-cards" element={<AppendixTestCards />} />
          <Route path="/merchant-login/acp" element={<MerchantLoginACP />} />
          <Route path="/merchant-login/acp/portal" element={<AcpPortalLogin />} />
          <Route path="/merchant-login/acq" element={<MerchantLoginACQ />} />
          <Route path="/merchant-login/acq/portal" element={<AcqPortalLogin />} />
          <Route path="/products" element={<ProductsHub />} />
          <Route path="/products/pos-card-machines" element={<ProductsPosCardMachines />} />
          <Route path="/products/epos-and-mini" element={<ProductsEposAndMini />} />
          <Route path="/products/softpos-and-mpos" element={<ProductsSoftPosMpos />} />
          <Route path="/products/ecommerce-solutions" element={<ProductsEcommerce />} />
          <Route path="/products/transaction-reporting" element={<ProductsTransactionReporting />} />
          <Route path="/get-started" element={<GetStartedQuote />} />
          <Route path="/get-started-epos" element={<GetStartedEpos />} />
          <Route path="/get-started-softpos" element={<GetStartedSoftpos />} />
          <Route path="/get-started-ecommerce" element={<GetStartedEcommerce />} />
          <Route path="/get-started-transaction-reporting" element={<GetStartedTransactionReporting />} />
          <Route path="/about/company" element={<AboutCompany />} />
          <Route path="/about/partner-with-us" element={<AboutPartner />} />
          <Route path="/about/social-responsibility" element={<AboutSocialResponsibility />} />
          <Route path="/about/modern-slavery" element={<AboutModernSlavery />} />
          <Route path="/about/press-and-media" element={<AboutPressMedia />} />
          <Route path="/contact-sales" element={<ContactSales />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/cookies-policy" element={<CookiePolicy />} />
          <Route path="/sca-statement" element={<SCAStatement />} />
          <Route path="/complaints-resolution" element={<ComplaintsResolution />} />
          <Route path="/about/ethical-business" element={<AboutModernSlavery />} />
        </Routes>
        <Toaster />
        <CookieConsent />
      </div>
    </BrowserRouter>
  );
}

export default App;